import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { OverlayTrigger, Tooltip, Button, Modal, Form } from "react-bootstrap";
import InfoIcon from "../../assets/img/info-user-icon.png";
import editIcon from "../../assets/img/edit-icon.png";
import deleteIcon from "../../assets/img/delete.png";
import crossIcon from "../../assets/img/cross-icon.png";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllConfiguration,
  deleteConfiguration,
  updateConfiguration,
  createConfiguration,
} from "../../redux/slice/getAllConfigurationListSlice";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useForm } from "react-hook-form";

function UserInviteLink({ configType, configName }) {
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, formState: { errors }, clearErrors } =
    useForm();
  const [configInputs, setConfigInputs] = useState({
    configName: "",
    configType: "",
    configDesc: "",
  });
  const [customErrorForConfigInputs, setCustomErrorForConfigInputs] = useState({configDesc: ""});
  const [configDeleteId, setConfigDeleteId] = useState(null);
  const [configUpdateId, setConfigUpdateId] = useState(null);
  const [showConfigDeleteModal, setShowConfigDeleteModal] = useState(false);
  const [showConfigUpdateModal, setShowConfigUpdateModal] = useState(false);
  const [editConfig, setEditConfig] = useState({
    name: "",
    value: "",
    type: "",
    desc: "",
  });
  const { configurations, status } = useSelector(
    (state) => state.Configurations
  );
  const canView = useHasPermission("Settings-view");
  const canEdit = useHasPermission("Settings-manage");
  const canDelete = useHasPermission("Settings-delete");

  useEffect(() => {
    if (canView) {
      dispatch(fetchAllConfiguration({ "config": configType }));
    }
  }, [dispatch, canView, configType]);

  if (!canView) {
    return (
      <p className="perm-msg">
        You don’t have permission to view settings page!
      </p>
    );
  }

  const handlePatternForConfigInputs = (e, pattern, field) => {
    let value = e.target.value;
    clearErrors(field);
    let patternErrorMessage = "";
    if ( field === "configDesc" && value && !pattern.test(value)) {
      patternErrorMessage =
        "No spaces, numbers or special characters are allowed";
    }
    setConfigInputs((prev) => ({ ...prev, [field]: value }));

    setCustomErrorForConfigInputs((prev) => ({
      ...prev,
      [field]: patternErrorMessage,
    }));
    if (!patternErrorMessage) {
      setCustomErrorForConfigInputs((prev) => ({ ...prev, [field]: "" }));
      clearErrors(field);
    }
  };

  const handleDeleteConfig = (configId) => {
    setConfigDeleteId(configId);
    setShowConfigDeleteModal(true);
  };

  const handleConfirmDeleteConfig = async () => {
    if (!canDelete) {
      toast.error("You don't have permission to delete configuration", {
        className: "toast-error-custom",
        autoClose: 5000,
      });
      return;
    }
    if (configDeleteId === null) return;
    try {
      await dispatch(deleteConfiguration(configDeleteId)).unwrap();
      setShowConfigDeleteModal(false);
      setConfigDeleteId(null);
      toast.success("Configuration deleted successfully!");
    } catch (error) {
      setShowConfigDeleteModal(false);
      setConfigDeleteId(null);
      toast.error(error?.message || "Failed to delete configuration!");
    }
  };

  const handleEditConfig = (config) => {
    setConfigUpdateId(config.id);
    setEditConfig({
      name: config.configName,
      value: config.configValue,
      type: config.configType,
      desc: config.description,
    });
    setShowConfigUpdateModal(true);
  };

  const handleUpdateConfig = async () => {
    if (!canEdit) {
      toast.error("You don't have permission to edit configuration", {
        className: "toast-error-custom",
        autoClose: 5000,
      });
      return;
    }
    if (configUpdateId === null) return;
    try {
      await dispatch(
        updateConfiguration({
          configId: configUpdateId,
          updatedData: {
            configName: editConfig.name,
            configValue: editConfig.value,
            configType: editConfig.type,
            description: editConfig.desc,
          },
        })
      ).unwrap();
      setShowConfigUpdateModal(false);
      await dispatch(fetchAllConfiguration({ "config": configType })).unwrap();
      setConfigUpdateId(null);
      toast.success("Configuration updated successfully!");
    } catch (error) {
      setShowConfigUpdateModal(false);
      setConfigUpdateId(null);
      toast.error(error?.message || "Failed to update configuration!");
    }
  };

  const handleFormSubmit = async (data) => {
    if (!canEdit) {
      toast.error("You don't have permission to save configuration", {
        className: "toast-error-custom",
        autoClose: 5000,
      });
      return;
    }

    const payload = {
      configName: data.configName,
      configValue: data.configValue,
      configType: configType,
      description: data.configDesc,
    };
    try {
      const response = await dispatch(createConfiguration(payload)).unwrap();
      await dispatch(fetchAllConfiguration({ "config": configType })).unwrap();
      toast.success(response.data.reason || "Configuration has been created sucessfully!", {
        className: "toast-success-custom",
      });
    } catch (err) {
      toast.error(err || "unable to connect to server", {
        className: "toast-error-custom",
      });
    }
    reset();
    setConfigInputs({ configName: "", configType: "", configDesc: "" });
  };

  const renderTooltip = (props, description) => (
    <Tooltip id="button-tooltip" {...props}>
      {description}
    </Tooltip>
  );

  return (
    <div>
      <div className="section-body m-0">
        <div className="container-fluid">
          <div className="settings-heading pt-3 pt-md-0">
            <h5>USER INVITE LINK</h5>
            <p>Set and manage expiration deadlines for candidate invitation links. This configuration ensures that registration forms are completed within a specific timeframe.</p>
          </div>
          <div className="col clearfix">
            <div className="col-lg-12 col-md-12">
              <div className="setting-container">
                <ul className="list-group mb-3">
                  <li className="list-group-item">
                    <div className="mb-0">
                      <div className="config-form p-0">
                        <form onSubmit={handleSubmit(handleFormSubmit)}>
                          <div>
                            <div className="input-text">
                              <p>
                                Config. Name{" "}
                                <span style={{ color: "red" }}>*</span>
                              </p>
                              <div className="user-input-icons">
                                <select
                                  className="input-field form-select-custom custom-dropdown"
                                  disabled={!canEdit}
                                  {...register("configName", {
                                    required: "Please select a Configuration Name",
                                  })}
                                >
                                  <option value="" hidden>
                                    Please select the Config. Name
                                  </option>

                                  {configName.map(configName => {
                                    return (<option key={configName} value={configName}>
                                      {configName?.replace(/_/g, " ")}
                                    </option>)
                                  })
                                  }
                                </select>
                                {errors.configName && (
                                  <div className="userErrorMessage">
                                    {errors.configName.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="input-text">
                              <p>
                                Config. Value{" "}
                                <span style={{ color: "red" }}>*</span>
                              </p>
                              <div className="user-input-icons">
                                <input
                                  className="input-field"
                                  type="number"
                                  min="0"
                                  max="120"
                                  value={configInputs.configValue}
                                  disabled={!canEdit}
                                  placeholder="Please enter the config. value"
                                  {...register("configValue", {
                                    required: "Please enter the config. value",
                                  })}
                                />
                                {errors.configValue && (
                                  <div className="userErrorMessage">
                                    {errors.configValue.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="input-text">
                              <p>
                                Config. Type{" "}
                                <span style={{ color: "red" }}>*</span>
                              </p>
                              <div className="user-input-icons">
                                <input
                                  className="input-field"
                                  type="text"
                                  value={configType?.replace(/_/g, " ")}
                                  disabled
                                  placeholder="Please enter its type"
                                  {...register("configType", {
                                    required:
                                      "Please enter the type of config",
                                  })}
                                />
                                {errors.configType && (
                                  <div className="userErrorMessage">
                                    {errors.configType.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="input-text">
                              <p>
                                Description{" "}
                              </p>
                              <div className="user-input-icons">
                                <input
                                  className="input-field"
                                  type="text"
                                  value={configInputs.configDesc}
                                  disabled={!canEdit}
                                  placeholder="Please enter its description"
                                  {...register("configDesc", {
                                    //required: "Please enter its description",
                                  })}
                                  onChange={(e) =>
                                    handlePatternForConfigInputs(
                                      e,
                                      /^.*$/,
                                      "configDesc"
                                    )
                                  }
                                />
                                {errors.configDesc && (
                                  <div className="userErrorMessage">
                                    {errors.configDesc.message}
                                  </div>
                                )}
                                {customErrorForConfigInputs.configDesc && (
                                  <div className="userErrorMessage">
                                    {customErrorForConfigInputs.configDesc}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-center mt-4">
                              <button
                                className="primary-btn"
                                type="submit"
                                title={
                                  canEdit
                                    ? "save configuration"
                                    : "No permission to save configuration"
                                }
                                disabled={!canEdit}
                              >
                                Save Configuration
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                      <ToastContainer />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-12 col-md-12">
              <div className="card">
                <div className="card-header">
                  <p className="card-title">
                    <b>Configuration List</b>
                  </p>
                </div>
                <div className="card-body">
                  <div className="row clearfix">
                    <div className="role-data table-responsive mt-0">
                      {status === "loading" && (
                        <p className="loading">Loading...</p>
                      )}

                      {status === "Failed" && (
                        <p className="server-error-msg">Server Error...</p>
                      )}
                      {status == "succeeded" &&
                        (configurations.length > 0 ? (
                          <table className="role-data-table table table-hover table-vcenter text-nowrap mb-0">
                            <thead>
                              <tr>
                                <th className="table-row">Type</th>
                                <th className="table-row">Name</th>
                                <th className="table-row">Value</th>

                                <th className="table-row">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {configurations.map((config) => (
                                <tr key={config.id}>
                                  <td>{config.configType?.replace(/_/g, " ")}</td>
                                  <td>
                                    {config.configName?.replace(/_/g, " ")}
                                    <OverlayTrigger
                                      placement="right"
                                      delay={{ show: 250, hide: 400 }}
                                      overlay={(props) =>
                                        renderTooltip(props, config.description)
                                      }
                                    >
                                      <img
                                        src={InfoIcon}
                                        alt=""
                                        style={{
                                          width: "15px",
                                          marginLeft: "10px",
                                        }}
                                      />
                                    </OverlayTrigger>
                                  </td>
                                  <td>{config.configValue}</td>

                                  <td className="action-column">
                                    <button
                                      className="edit-role"
                                      title={
                                        canEdit
                                          ? "edit configuration"
                                          : "No permisssion to edit configuration"
                                      }
                                      disabled={!canEdit}
                                      onClick={
                                        canEdit
                                          ? () => handleEditConfig(config)
                                          : undefined
                                      }
                                    >
                                      <img
                                        src={editIcon}
                                        alt="edit"
                                        style={{ opacity: canEdit ? 1 : 0.5 }}
                                      />
                                    </button>
                                    <button
                                      className="delete-role"
                                      title={
                                        canDelete
                                          ? "delete configuration"
                                          : "No permisssion to delete configuration"
                                      }
                                      disabled={!canDelete}
                                      onClick={
                                        canDelete
                                          ? () => handleDeleteConfig(config.id)
                                          : undefined
                                      }
                                    >
                                      <img
                                        src={deleteIcon}
                                        alt=""
                                        className="action-icon"
                                        style={{ opacity: canDelete ? 1 : 0.5 }}
                                      />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="loading">No configuration record found</p>
                        ))}
                    </div>
                  </div>
                  {/* modal for delete config */}
                  <Modal
                    show={showConfigDeleteModal}
                    onHide={() => setShowConfigDeleteModal(false)}
                  >
                    <Modal.Header>
                      <Modal.Title>Delete Configuration</Modal.Title>
                      <button
                        onClick={() => setShowConfigDeleteModal(false)}
                        className="modal-close"
                      >
                        <img
                          src={crossIcon}
                          alt="Close"
                          style={{ width: "20px", height: "20px" }}
                        />
                      </button>
                    </Modal.Header>
                    <Modal.Body>
                      Are you sure you want to delete this Configuration?
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="cancel-cta"
                        onClick={() => setShowConfigDeleteModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="danger"
                        disabled={!canDelete}
                        onClick={
                          canDelete ? handleConfirmDeleteConfig : undefined
                        }
                      >
                        Delete
                      </Button>
                    </Modal.Footer>
                  </Modal>
                  {/* modal for update config */}
                  <Modal
                    show={showConfigUpdateModal}
                    onHide={() => setShowConfigUpdateModal(false)}
                  >
                    <Modal.Header>
                      <Modal.Title>Edit Config</Modal.Title>
                      <button
                        onClick={() => setShowConfigUpdateModal(false)}
                        className="modal-close"
                      >
                        <img
                          src={crossIcon}
                          alt="Close"
                          style={{ width: "20px", height: "20px" }}
                        />
                      </button>
                    </Modal.Header>
                    <Modal.Body>
                      <Form>
                        <Form.Group>
                          <Form.Label>Config. Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={editConfig.name}
                            disabled
                            onChange={(e) =>
                              setEditConfig({
                                ...editConfig,
                                name: e.target.value,
                              })
                            }
                            placeholder="Please enter the config. name"
                          />
                        </Form.Group>
                        <Form.Group className="mt-3">
                          <Form.Label>Config. Value </Form.Label>
                          <Form.Control
                            type="number"
                            value={editConfig.value}
                            onChange={(e) =>
                              setEditConfig({
                                ...editConfig,
                                value: e.target.value,
                              })
                            }
                            placeholder="Please enter the config. value"
                          />
                        </Form.Group>
                        <Form.Group className="mt-3">
                          <Form.Label>Config. Type </Form.Label>
                          <Form.Control
                            type="text"
                            value={editConfig.type}
                            disabled
                            onChange={(e) =>
                              setEditConfig({
                                ...editConfig,
                                type: e.target.value,
                              })
                            }
                            placeholder="Please enter its type"
                          />
                        </Form.Group>
                        <Form.Group className="mt-3">
                          <Form.Label>Config. Description </Form.Label>
                          <Form.Control
                            type="text"
                            value={editConfig.desc}
                            onChange={(e) =>
                              setEditConfig({
                                ...editConfig,
                                desc: e.target.value,
                              })
                            }
                            placeholder="Please enter its description"
                          />
                        </Form.Group>
                      </Form>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="cancel-cta"
                        onClick={() => setShowConfigUpdateModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="update-cta"
                        disabled={!canEdit}
                        onClick={canEdit ? handleUpdateConfig : undefined}
                      >
                        Update
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserInviteLink
